import React, {
  FC,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState
} from "react";
import styled from "styled-components";

import {
  SUBTITLES_BACKGROUND_COLOR,
  SUBTITLES_FONT_SIZE_MAX,
  SUBTITLES_FONT_SIZE_MIN,
  SUBTITLES_FONT_SIZE_SQUAREVIDEO_MULTIPLIER,
  SUBTITLES_FONT_SIZE_WIDTH_BASED_MULTIPLIER,
  SUBTITLES_LINE_HEIGHT_MULTIPLIER
} from "../../constants";
import { useEmbedParametersContext } from "../../contexts/EmbedParametersContext";
import { SUBTITLES_BACKGROUND } from "../../enums";
import i18n from "../../service/i18n";
import { SubtitleCueData } from "../../store/slices/tracksSlice";
import { usePlayerStore } from "../../store/usePlayerStore";

const isSquareContainer = playerContainerSize =>
  playerContainerSize.h === playerContainerSize.w;

const isVerticalContainer = (playerContainerSize: ElementSize) =>
  playerContainerSize.h > playerContainerSize.w;

const isHorizontalOrSquareVideoInPortraitMode = (
  playerContainerSize: ElementSize,
  aspectRatio?: VideoAspectRatio
) => {
  const isHorizontalVideo = aspectRatio
    ? aspectRatio.w >= aspectRatio.h
    : false;

  return isVerticalContainer(playerContainerSize) && isHorizontalVideo;
};

const parseAspectRatio = (
  aspectRatio?: string
): VideoAspectRatio | undefined => {
  if (aspectRatio) {
    const splittedRatio = aspectRatio.split(":");
    return {
      h: parseInt(splittedRatio[1], 10),
      w: parseInt(splittedRatio[0], 10)
    };
  }
};

const resolveFontSize = (
  subtitlesSizeMultiplier: number,
  playerContainerSize: ElementSize
) => {
  const responsiveFontSize =
    Math.max(playerContainerSize.h, playerContainerSize.w) *
    SUBTITLES_FONT_SIZE_WIDTH_BASED_MULTIPLIER;

  let fontSize =
    responsiveFontSize > SUBTITLES_FONT_SIZE_MAX
      ? SUBTITLES_FONT_SIZE_MAX
      : responsiveFontSize < SUBTITLES_FONT_SIZE_MIN
        ? SUBTITLES_FONT_SIZE_MIN
        : responsiveFontSize;

  if (isSquareContainer(playerContainerSize)) {
    fontSize *= SUBTITLES_FONT_SIZE_SQUAREVIDEO_MULTIPLIER;
  }

  fontSize *= subtitlesSizeMultiplier;

  return Math.round(fontSize);
};

const resolveSubtitlesPosition = (
  isControlsVisible: boolean,
  isMobile: boolean,
  playerContainerSize?: ElementSize,
  aspectRatio?: VideoAspectRatio
) => {
  const minPosition = isControlsVisible ? (isMobile ? 32 : 80) : 0;

  if (
    playerContainerSize &&
    aspectRatio &&
    isHorizontalOrSquareVideoInPortraitMode(playerContainerSize, aspectRatio)
  ) {
    const videoHeight = (playerContainerSize.w / aspectRatio.w) * aspectRatio.h;
    const videoBottomBorderPosition =
      playerContainerSize.h / 2 - videoHeight / 2;

    return Math.max(minPosition, videoBottomBorderPosition);
  }

  return minPosition;
};

const resolveMarginBottom = (
  isFullscreen: boolean,
  isMobile: boolean,
  playerContainerSize: ElementSize,
  aspectRatio?: VideoAspectRatio
) => {
  if (
    isHorizontalOrSquareVideoInPortraitMode(playerContainerSize, aspectRatio)
  ) {
    return isMobile ? (isFullscreen ? 16 : 8) : 32;
  }
  if (isVerticalContainer(playerContainerSize)) {
    return 32;
  }
  if (isMobile) {
    return isSquareContainer(playerContainerSize) || isFullscreen ? 16 : 8;
  }
  if (isSquareContainer(playerContainerSize) || playerContainerSize.w > 1000) {
    return 32;
  }
  return 16;
};

const calculateCueVTTPosition = (
  line: number | "auto",
  snapToLines: boolean,
  containerHeight: number,
  defaultPosition: number,
  fontSize: number | undefined
): number => {
  if (line === "auto") {
    return defaultPosition;
  }

  if (snapToLines) {
    const lineHeightPx =
      (fontSize || SUBTITLES_FONT_SIZE_MIN) * SUBTITLES_LINE_HEIGHT_MULTIPLIER;

    if (typeof line === "number") {
      // Treat line 0 as line 1 (first line from top)
      const normalizedLine = line === 0 ? 1 : line;

      if (normalizedLine < 0) {
        return Math.abs(normalizedLine) * lineHeightPx;
      } else {
        return containerHeight - normalizedLine * lineHeightPx;
      }
    }
  } else {
    const percent = Number(line);
    if (!isNaN(percent)) {
      return containerHeight - containerHeight * (percent / 100);
    }
  }

  return defaultPosition;
};

const resolveVideoImageSize = (
  playerContainerSize?: ElementSize,
  aspectRatio?: VideoAspectRatio
): ElementSize | undefined => {
  if (!playerContainerSize) return undefined;
  if (!aspectRatio) return playerContainerSize;

  const containerRatio =
    Math.round((playerContainerSize.w / playerContainerSize.h) * 100) / 100;
  const imageRatio = Math.round((aspectRatio.w / aspectRatio.h) * 100) / 100;

  if (containerRatio === imageRatio) {
    return playerContainerSize;
  } else if (containerRatio > imageRatio) {
    // The container is wider than the video image
    return {
      h: playerContainerSize.h,
      w: Math.round(playerContainerSize.h * imageRatio)
    };
  } else {
    return {
      h: Math.round(playerContainerSize.w / imageRatio),
      w: playerContainerSize.w
    };
  }
};

const SubtitleRow = styled.div<{
  subtitlesBackground: SUBTITLES_BACKGROUND;
}>`
  background-color: ${({ subtitlesBackground }) =>
    SUBTITLES_BACKGROUND_COLOR[subtitlesBackground]};

  /* Note: Subtitle color is white unless background is white */
  color: ${({ subtitlesBackground: subtitlesBackgroundColor }) =>
    subtitlesBackgroundColor === SUBTITLES_BACKGROUND.WHITE
      ? "black"
      : "white"};
  font-family: YleTiresias, sans-serif !important;
  line-height: normal;
  pointer-events: all;
  text-align: center;
  text-shadow: ${({ subtitlesBackground: subtitlesBackgroundColor }) =>
    subtitlesBackgroundColor === SUBTITLES_BACKGROUND.NONE
      ? "1px 1px 0 #000, -1px -1px 0 #000, 1px -1px 0 #000, -1px 1px 0 #000"
      : "none"};
  transition: bottom 0.3s ease-in-out;
  white-space: pre-line;
  padding: 0.05em 0.25em;
`;

const PositionedSubtitleRow = styled(SubtitleRow)`
  position: absolute;
`;

const SubtitlesWrapper = styled.section`
  width: 100%;
`;

const LiveRegion = styled.div`
  align-items: center;
  display: flex;
  flex-direction: column;
  pointer-events: none;
  position: absolute;
  width: 100%;
`;

type ElementSize = {
  h: number;
  w: number;
};

type VideoAspectRatio = { w: number; h: number };

const resolveRowBackground = (
  cue: string,
  subtitlesBackground: SUBTITLES_BACKGROUND
): SUBTITLES_BACKGROUND =>
  cue.trim().length === 0 ? SUBTITLES_BACKGROUND.NONE : subtitlesBackground;

const RegularSubtitleRow = ({
  cue,
  subtitlesBackground
}: {
  cue: string;
  subtitlesBackground: SUBTITLES_BACKGROUND;
}) => (
  <SubtitleRow
    data-testid="subtitle-row"
    subtitlesBackground={resolveRowBackground(cue, subtitlesBackground)}>
    {cue}
  </SubtitleRow>
);

const VTTPositionedSubtitleRow = ({
  cue,
  fontSize,
  playerContainerHeight,
  subtitlesBackground,
  subtitlesPosition
}: {
  cue: SubtitleCueData;
  fontSize: number | undefined;
  subtitlesBackground: SUBTITLES_BACKGROUND;
  playerContainerHeight: number;
  subtitlesPosition: number;
}) => {
  const cuePosition = calculateCueVTTPosition(
    cue.line,
    cue.snapToLines,
    playerContainerHeight,
    subtitlesPosition,
    fontSize
  );

  return (
    <PositionedSubtitleRow
      data-testid="subtitle-row"
      subtitlesBackground={resolveRowBackground(cue.text, subtitlesBackground)}
      style={{ bottom: `${cuePosition}px` }}>
      {cue.text}
    </PositionedSubtitleRow>
  );
};

export const Subtitles: FC = () => {
  const { useStoreState } = usePlayerStore();
  const aspectRatio = useStoreState.aspectRatio();
  const device = useStoreState.device();
  const isControlsVisible = useStoreState.isControlsVisible();
  const isFullscreen = useStoreState.isFullscreen();
  const playerElement = useStoreState.playerElement();
  const subtitlesBackground = useStoreState.subtitlesBackground();
  const subtitlesCue = useStoreState.subtitlesCue();
  const subtitlesCues = useStoreState.subtitlesCues();
  const subtitlesSizeMultiplier = useStoreState.subtitlesSizeMultiplier();
  const useVTTPositioning = useStoreState.useVTTPositioning();

  const { alwaysFocusScreenReaderSubtitles, language } =
    useEmbedParametersContext();

  const [playerContainerSize, setPlayerContainerSize] = useState<ElementSize>();
  const [videoImageSize, setVideoImageSize] = useState<ElementSize>();
  const [focused, setFocused] = useState(false);

  const isMounted = useRef(true);

  const aspectRatioParsed = useMemo(() => {
    return parseAspectRatio(aspectRatio);
  }, [aspectRatio]);

  const resolvePlayerContainerSize = useCallback(() => {
    if (playerElement) {
      const observer = new IntersectionObserver(nodes => {
        observer.disconnect();
        const rect = nodes[0].boundingClientRect;
        if (isMounted.current) {
          setPlayerContainerSize({
            h: Math.round(rect.height),
            w: Math.round(rect.width)
          });
        }
      });
      observer.observe(playerElement);
    }
  }, [playerElement]);

  useEffect(() => {
    if (playerElement) {
      resolvePlayerContainerSize();
      window.addEventListener("resize", resolvePlayerContainerSize);

      return () => {
        isMounted.current = false;
        window.removeEventListener("resize", resolvePlayerContainerSize);
      };
    }
  }, [playerElement, resolvePlayerContainerSize]);

  useEffect(() => {
    // This fixes font size after a call to player api setAspectRatio()
    setTimeout(resolvePlayerContainerSize);
  }, [aspectRatio, resolvePlayerContainerSize]);

  useEffect(resolvePlayerContainerSize, [
    isFullscreen,
    resolvePlayerContainerSize
  ]);

  useEffect(() => {
    setVideoImageSize(
      resolveVideoImageSize(playerContainerSize, aspectRatioParsed)
    );
  }, [aspectRatioParsed, playerContainerSize]);

  const fontSize = useMemo(() => {
    if (videoImageSize) {
      return resolveFontSize(subtitlesSizeMultiplier, videoImageSize);
    }
  }, [videoImageSize, subtitlesSizeMultiplier]);

  const marginBottom = useMemo(() => {
    if (playerContainerSize) {
      return resolveMarginBottom(
        isFullscreen,
        device.isMobile,
        playerContainerSize,
        aspectRatioParsed
      );
    }
    return 0;
  }, [isFullscreen, device, playerContainerSize, aspectRatioParsed]);

  const subtitlesPosition = useMemo(() => {
    return resolveSubtitlesPosition(
      isControlsVisible,
      device.isMobile,
      playerContainerSize,
      aspectRatioParsed
    );
  }, [isControlsVisible, device, playerContainerSize, aspectRatioParsed]);

  const onFocus = () => setFocused(true);
  const onBlur = () => setFocused(false);

  const shouldUseVttPositioning = useVTTPositioning && subtitlesCues.length > 0;

  return playerContainerSize ? (
    <SubtitlesWrapper
      aria-label={i18n("tekstitykset", { language })}
      data-testid="subtitles-wrapper"
      onBlur={onBlur}
      onFocus={onFocus}
      role="region"
      style={fontSize ? { fontSize: `${fontSize}px` } : undefined}
      tabIndex={0}>
      <LiveRegion
        aria-live={
          focused || alwaysFocusScreenReaderSubtitles ? "polite" : "off"
        }
        aria-atomic="true"
        style={{
          bottom: `${shouldUseVttPositioning ? 0 : subtitlesPosition}px`,
          marginBottom: `${marginBottom}px`
        }}>
        {shouldUseVttPositioning
          ? subtitlesCues.map((cue, index) => (
              <VTTPositionedSubtitleRow
                key={cue.text + index}
                cue={cue}
                fontSize={fontSize}
                subtitlesBackground={subtitlesBackground}
                playerContainerHeight={playerContainerSize.h}
                subtitlesPosition={subtitlesPosition}
              />
            ))
          : subtitlesCue &&
            subtitlesCue
              .split("\n")
              .map((cue, index) => (
                <RegularSubtitleRow
                  key={cue + index}
                  cue={cue}
                  subtitlesBackground={subtitlesBackground}
                />
              ))}
      </LiveRegion>
    </SubtitlesWrapper>
  ) : null;
};
